/**
 * 导出功能工具函数
 */
import * as XLSX from "xlsx";

/**
 * 导出Excel文件
 * @param {Array} resultList - 计算结果列表
 * @param {Object} form - 表单数据
 */
export function exportExcel(resultList, form, discountSummary = null) {
  const isDiscount = discountSummary !== null;

  const header = isDiscount
    ? ["参与人", "原始金额（元）", "优惠分摊（元）", "实付金额（元）", "应付金额（元）", "差额类型", "最终金额（元）"]
    : ["参与人", "实付金额（元）", "应付金额（元）", "差额类型", "最终金额（元）"];

  const dataRows = resultList.map(item => {
    if (isDiscount) {
      return [item.name, item.originalAmount, item.discountAllocated, item.payAmount, item.shouldPay, item.type, item.finalAmount];
    }
    return [item.name, item.payAmount, item.shouldPay, item.type, item.finalAmount];
  });

  const exportData = [
    header,
    ...dataRows,
    [],
    ["计算时间", new Date().toLocaleString()],
    ["总金额", form.totalAmount]
  ];

  if (isDiscount) {
    exportData.push(["优惠摘要", ""]);
    exportData.push(["原价", discountSummary.totalOriginal]);
    if (discountSummary.thresholdDiscount > 0) exportData.push(["满减", `-${discountSummary.thresholdDiscount}`]);
    if (discountSummary.couponDiscount > 0) exportData.push(["券优惠", `-${discountSummary.couponDiscount}`]);
    exportData.push(["最终实付", discountSummary.finalTotal]);
  }

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(exportData);

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "费用分摊结果");

  // 生成文件名
  const fileName = `费用分摊结果_${new Date().toLocaleString().replace(/[/:\s]/g, '-')}.xlsx`;

  // 导出文件
  XLSX.writeFile(workbook, fileName);
}

/**
 * 导出TXT文件
 * @param {Array} resultList - 计算结果列表
 * @param {Object} form - 表单数据
 */
export function exportTXT(resultList, form, discountSummary = null) {
  const isDiscount = discountSummary !== null;

  let content = "费用分摊计算结果\n";
  content += "====================\n";
  content += `计算时间：${new Date().toLocaleString()}\n`;
  content += `总金额：${isDiscount ? discountSummary.totalOriginal : form.totalAmount}元\n`;

  if (isDiscount) {
    content += "优惠摘要：\n";
    content += `  原价：${discountSummary.totalOriginal}元\n`;
    if (discountSummary.thresholdDiscount > 0) content += `  满减：-${discountSummary.thresholdDiscount}元\n`;
    if (discountSummary.couponDiscount > 0) content += `  券优惠：-${discountSummary.couponDiscount}元\n`;
    content += `  最终实付：${discountSummary.finalTotal}元\n`;
  }

  content += "====================\n";

  if (isDiscount) {
    content += "参与人\t原始金额（元）\t优惠分摊（元）\t实付金额（元）\t应付金额（元）\t差额类型\t最终金额（元）\n";
    resultList.forEach(item => {
      content += `${item.name}\t${item.originalAmount}\t${item.discountAllocated}\t${item.payAmount}\t${item.shouldPay}\t${item.type}\t${item.finalAmount}\n`;
    });
  } else {
    content += "参与人\t实付金额（元）\t应付金额（元）\t差额类型\t最终金额（元）\n";
    resultList.forEach(item => {
      content += `${item.name}\t${item.payAmount}\t${item.shouldPay}\t${item.type}\t${item.finalAmount}\n`;
    });
  }

  // 生成文件名
  const fileName = `费用分摊结果_${new Date().toLocaleString().replace(/[/:\s]/g, '-')}.txt`;

  // 创建Blob对象
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });

  // 生成下载链接
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;

  // 触发下载
  document.body.appendChild(link);
  link.click();

  // 释放资源
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 格式化结算结果为纯文本（用于剪贴板分享）
 * @param {Array} resultList
 * @param {Object} form
 * @param {string} mode - 'simple' | 'discount'
 * @returns {string}
 */
export function formatResultAsText(resultList, form, discountSummary = null, mode = 'simple') {
  const isDiscount = mode === 'discount' && discountSummary !== null;

  let text = '费用分摊计算结果\n';
  text += '====================\n';
  text += `计算时间：${new Date().toLocaleString()}\n`;
  text += `总金额：${isDiscount ? discountSummary.totalOriginal : form.totalAmount}元\n`;

  if (isDiscount) {
    text += `\n【拼单满减详情】\n`;
    text += `分摊策略：${strategyLabel(form.discountRules.strategy)}\n`;
    text += `原价：${discountSummary.totalOriginal}元\n`;
    if (discountSummary.thresholdDiscount > 0) text += `满减：-${discountSummary.thresholdDiscount}元\n`;
    if (discountSummary.couponDiscount > 0) text += `券优惠：-${discountSummary.couponDiscount}元\n`;
    text += `最终实付：${discountSummary.finalTotal}元\n`;
    const thresholds = (form.discountRules.thresholds || []).filter(t => t.threshold > 0);
    if (thresholds.length > 0) {
      thresholds.forEach(t => {
        text += `满减规则：满${t.threshold}减${t.discount}\n`;
      });
    }
    const coupons = (form.discountRules.coupons || []).filter(c => c.amount > 0);
    if (coupons.length > 0) {
      coupons.forEach(c => {
        text += `优惠券：${c.name} -${c.amount}元`;
        if (c.applicablePerson) text += `（${c.applicablePerson}专用）`;
        text += '\n';
      });
    }
  }

  text += '\n';
  if (isDiscount) {
    text += '参与人  原始金额  优惠分摊  实付金额  应付金额  差额类型  最终金额\n';
    text += '----------------------------------------------------------------\n';
    resultList.forEach(item => {
      text += `${item.name}  ${item.originalAmount}  ${item.discountAllocated}  ${item.payAmount}  ${item.shouldPay}  ${item.type}  ${item.finalAmount}\n`;
    });
  } else {
    text += '参与人  实付金额  应付金额  差额类型  最终金额\n';
    text += '------------------------------------------------\n';
    resultList.forEach(item => {
      text += `${item.name}  ${item.payAmount}  ${item.shouldPay}  ${item.type}  ${item.finalAmount}\n`;
    });
  }

  return text;
}

function strategyLabel(strategy) {
  const map = {
    proportional: '按金额比例分摊',
    perPerson: '人均分摊',
    organizerFirst: '发起人优先'
  };
  return map[strategy] || strategy;
}
